import { BuyInvoiceApi } from "./BuyInvoiceApi";
import { CreditnoteApi } from "./CreditnoteApi";
import { DebitnoteApi } from "./DebitnoteApi";
import { InvoiceApi } from "./InvoiceApi";
import { EDocumentSourceModel } from "@/formTypes/EDocumentReportDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { LineDiagnosticOptions, ReceptorClientInput, ReceptorDiagnosticInput } from "@utils/edocumentDiagnostic";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { CreditnoteFormDataKeys } from "@/generated/CreditnoteFormDataKeys";
import { DebitnoteFormDataKeys } from "@/generated/DebitnoteFormDataKeys";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import { DebitnoteRelatedAttributes } from "@/formTypes/DebitnoteDataType";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";

/** Modelos cuyo rechazo se puede diagnosticar: los que este sistema emite. */
export type DiagnosableModel = Extract<
    EDocumentSourceModel,
    "Invoice" | "Creditnote" | "Debitnote" | "BuyInvoice"
>;

export type EDocumentDiagnosticSource = {
    header: ReceptorDiagnosticInput;
    lines: EDocumentProductDataType[];
    client?: ReceptorClientInput | null;
    clientId?: string;
    /** Contexto que cambia la tarifa esperada de las lineas (regimen del receptor, exportacion). */
    lineOptions?: LineDiagnosticOptions;
};

export const isDiagnosableModel = (model?: string | null): model is DiagnosableModel =>
    model === "Invoice" || model === "Creditnote" || model === "Debitnote" || model === "BuyInvoice";

/**
 * Trae el documento con lo que necesita el diagnostico: las lineas con su producto del catalogo
 * (de ahi sale el CAByS que realmente viaja a Hacienda) y el cliente vigente, porque el telefono
 * del XML se lee del maestro de clientes y no del snapshot del documento.
 */
export const loadDocumentForDiagnostic = async (
    model: DiagnosableModel,
    id: string
): Promise<EDocumentDiagnosticSource> => {
    switch (model) {
        case "Creditnote": {
            const note = await CreditnoteApi.get(id, {
                selectionSet: [
                    ...CreditnoteFormDataKeys.filter((k) => !CreditnoteRelatedAttributes.includes(k)),
                    "client.*",
                    "creditnoteProducts.*",
                    "creditnoteProducts.product.*",
                ],
            });
            return {
                header: note,
                lines: (note.creditnoteProducts || []) as EDocumentProductDataType[],
                client: note.client,
                clientId: note.clientId ? `${note.clientId}` : undefined,
                // La nota no guarda el regimen: copia las lineas de la factura del mismo cliente.
                lineOptions: { receptorTaxRegime: note.client?.tax_regime },
            };
        }
        case "Debitnote": {
            const note = await DebitnoteApi.get(id, {
                selectionSet: [
                    ...DebitnoteFormDataKeys.filter((k) => !DebitnoteRelatedAttributes.includes(k)),
                    "client.*",
                    "debitnoteProducts.*",
                    "debitnoteProducts.product.*",
                ],
            });
            return {
                header: note,
                lines: (note.debitnoteProducts || []) as EDocumentProductDataType[],
                client: note.client,
                clientId: note.clientId ? `${note.clientId}` : undefined,
                lineOptions: { receptorTaxRegime: note.client?.tax_regime },
            };
        }
        case "BuyInvoice": {
            // La factura de compra no manda `Client` a la lambda: su receptor sale del snapshot.
            const buyInvoice = await BuyInvoiceApi.get(id, {
                selectionSet: [
                    ...BuyInvoiceFormDataKeys.filter((k) => !buyInvoiceRelatedAttributes.includes(k)),
                    "buyInvoiceProducts.*",
                    "buyInvoiceProducts.product.*",
                ],
            });
            return {
                header: buyInvoice,
                lines: (buyInvoice.buyInvoiceProducts || []) as EDocumentProductDataType[],
                client: null,
            };
        }
        case "Invoice":
        default: {
            const invoice = await InvoiceApi.get(id, {
                selectionSet: [
                    ...InvoiceFormDataKeys.filter((k) => !InvoiceRelatedAttributes.includes(k)),
                    "client.*",
                    "invoiceProducts.*",
                    "invoiceProducts.product.*",
                ],
            });
            return {
                header: invoice,
                lines: (invoice.invoiceProducts || []) as EDocumentProductDataType[],
                client: invoice.client,
                clientId: invoice.clientId ? `${invoice.clientId}` : undefined,
                lineOptions: {
                    receptorTaxRegime: invoice.tax_regime,
                    documentType: invoice.einvoice_kind,
                },
            };
        }
    }
};

export const EDocumentDiagnosticApi = {
    loadDocumentForDiagnostic,
};
